import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { ArrowLeft, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { KbAttachmentsCard } from '@/components/knowledge/KbAttachments';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormattedText } from '@/components/ui/FormattedText';
import { toast } from '@/store/toastStore';
import {
  deleteKnowledgeBaseDocument,
  getKnowledgeBaseDocument,
  kbQueryKeys,
} from '@/services/knowledge-base.service';

export function DocumentViewer() {
  const { id } = useParams<{ id: string }>();
  const documentId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const docQuery = useQuery({
    queryKey: kbQueryKeys.document(documentId),
    queryFn: () => getKnowledgeBaseDocument(documentId),
    enabled: documentId > 0,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteKnowledgeBaseDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.all });
      toast.success('Document deleted');
      navigate('..');
    },
    onError: () => toast.error('Failed to delete document'),
  });

  if (docQuery.isLoading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>;
  }

  const doc = docQuery.data;
  if (!doc) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="text-base font-semibold text-slate-900">Document not found</h2>
        <Link to=".." className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
          <ArrowLeft className="h-4 w-4" />
          Back to library
        </Link>
      </div>
    );
  }

  const sourceLabel =
    doc.source?.sourceType === 'manual'
      ? 'Manual'
      : doc.source?.sourceType === 'delightree_zapier'
        ? 'Delightree (synced)'
        : doc.source?.sourceType === 'zapier'
          ? 'Zapier (synced)'
          : (doc.source?.name ?? 'Unknown');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link to=".." className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-4 w-4" />
            Back to library
          </Link>
          <h2 className="mt-2 text-xl font-semibold text-slate-900">{doc.title}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <Badge variant={doc.editable ? 'info' : 'neutral'}>{sourceLabel}</Badge>
            {doc.category && <Badge variant="neutral">{doc.category}</Badge>}
            {doc.tags.map((t) => (
              <span key={t} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                {t}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {doc.url && (
            <a
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              Open original
            </a>
          )}
          {doc.editable && (
            <>
              <Link to="edit">
                <Button variant="secondary" leftIcon={<Pencil className="h-4 w-4" />}>
                  Edit
                </Button>
              </Link>
              <Button
                variant="danger"
                leftIcon={<Trash2 className="h-4 w-4" />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {!doc.editable && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          This document is synced from {sourceLabel} and is read-only here. Edits would be
          overwritten on the next sync. To take over editing, pause this connection on the
          Connections tab.
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        {doc.body ? (
          doc.bodyFormat === 'html' ? (
            <div
              className="prose prose-sm max-w-none text-slate-800 [&_img]:max-w-full [&_img]:rounded-md"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.body) }}
            />
          ) : (
            <FormattedText text={doc.body} className="prose prose-sm max-w-none text-slate-800" />
          )
        ) : (
          <p className="text-sm italic text-slate-400">This document has no body content.</p>
        )}
      </div>

      <KbAttachmentsCard attachments={doc.attachments ?? []} />

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          deleteMutation.mutate();
          setConfirmDelete(false);
        }}
        title="Delete document?"
        message="This permanently removes the document and its search chunks. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
